import React, { useState, useMemo } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { formatPrice } from "../../utils/formatPrice";
import "../../css/models-common.css";
import "../../css/search.css";
import useBreakpoint from "../../utils/useBreakPoint";
import { useProductSearch } from "../../hooks";
import MiniChartSVG from "../../component/models/MiniChartSVG";
import { translateSectorLabel } from "../../utils/sectorLabel";

const ModelSearchResults = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const breakpoint = useBreakpoint();
    const { t, i18n } = useTranslation();
    const searchQuery = searchParams.get("q") || "";
    const searchType = searchParams.get("type") || "text"; // 'text' or 'ai'

    // Fetch search results from API
    const { data: searchData, loading, error } = useProductSearch(searchQuery, searchType);

    // View mode: 'list' or 'grid'
    const [viewMode, setViewMode] = useState("list");

    // Selected category for filtering
    const [selectedCategory, setSelectedCategory] = useState(null);

    // AI summary expand/collapse state
    const [aiSummaryExpanded, setAiSummaryExpanded] = useState(false);

    // Transform search results to match expected format
    // Hook returns flattened: { query, results: [{ id, name, ..., sectors, is_owner }] }
    const searchResults = useMemo(() => {
        if (!searchData) return [];

        const results = searchData.results || [];

        return results.map(product => ({
            id: product.id,
            name: product.name,
            description: product.description || '',
            date: product.last_price_date
                ? (() => {
                    const [year, month, day] = product.last_price_date.split('-');
                    return `${day}.${month}.${year}`;
                  })()
                : '',
            value: product.last_price_value || 0,
            currencyUnit: `${product.last_price_currency || ''} / ${product.last_price_unit || ''}`,
            chartData: product.chart_data || [],
            sectors: product.sectors || [],
            is_owner: product.is_owner,
        }));
    }, [searchData]);

    // AI summary — not currently returned by the products prompt endpoint
    const aiSummary = useMemo(() => {
        if (searchType === 'ai' && searchData?.summary) {
            return searchData.summary;
        }
        return '';
    }, [searchData, searchType]);

    // Derive matching categories from sectors in search results
    const matchingCategories = useMemo(() => {
        if (!searchData?.results) return [];

        const sectorMap = new Map();
        searchData.results.forEach(product => {
            (product.sectors || []).forEach(sector => {
                if (sectorMap.has(sector.id)) {
                    sectorMap.get(sector.id).count++;
                } else {
                    sectorMap.set(sector.id, { id: sector.id, name: sector.name, count: 1 });
                }
            });
        });
        return Array.from(sectorMap.values());
    }, [searchData]);

    // Handle category click
    const handleCategoryClick = (categoryName) => {
        setSelectedCategory(prev => prev === categoryName ? null : categoryName);
    };

    // Handle model item click
    const handleModelClick = (model) => {
        navigate(`/models/model/${model.id}`);
    };

    return (
        <div className="search-results-container">
            <div className="search-content-wrapper">
                {/* Main Content Area */}
                <div className="search-main-content">
                    {/* Header */}
                    <div className="search-header">
                        <h2 className="search-title">{t('models.search.title')}</h2>
                        {!loading && (
                            <div className="view-toggle">
                                <button
                                    className={`view-toggle-btn ${viewMode === "grid" ? "active" : ""}`}
                                    onClick={() => setViewMode("grid")}
                                    aria-label="Grid view"
                                >
                                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                                        <rect x="2" y="2" width="6" height="6" rx="1" stroke="currentColor" strokeWidth="1.5" />
                                        <rect x="12" y="2" width="6" height="6" rx="1" stroke="currentColor" strokeWidth="1.5" />
                                        <rect x="2" y="12" width="6" height="6" rx="1" stroke="currentColor" strokeWidth="1.5" />
                                        <rect x="12" y="12" width="6" height="6" rx="1" stroke="currentColor" strokeWidth="1.5" />
                                    </svg>
                                </button>
                                <button
                                    className={`view-toggle-btn ${viewMode === "list" ? "active" : ""}`}
                                    onClick={() => setViewMode("list")}
                                    aria-label="List view"
                                >
                                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                                        <rect x="2" y="3" width="16" height="2" rx="1" fill="currentColor" />
                                        <rect x="2" y="9" width="16" height="2" rx="1" fill="currentColor" />
                                        <rect x="2" y="15" width="16" height="2" rx="1" fill="currentColor" />
                                    </svg>
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Loading State */}
                    {loading && (
                        <div className="search-loading">
                            <div className="loading-spinner"></div>
                            <p>{t('models.search.searching')} "{searchQuery}"...</p>
                        </div>
                    )}

                    {/* Search query display */}
                    {!loading && searchQuery && (
                        <div className="search-query-display">
                            {t('models.search.showing')} <span className="query-text">"{searchQuery}"</span>
                        </div>
                    )}

                    {/* AI Summary Section - Only for AI search type */}
                    {!loading && searchType === "ai" && aiSummary && (
                        <div className="ai-summary-section">
                            <div className="ai-summary-header">
                                <div className="d-flex align-items-center gap-2">
                                    {/* Magic Star Icon SVG */}
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M12 2L14.39 9.61L22 12L14.39 14.39L12 22L9.61 14.39L2 12L9.61 9.61L12 2Z"
                                            stroke="url(#paint0_linear)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                        <defs>
                                            <linearGradient id="paint0_linear" x1="2" y1="2" x2="22" y2="22" gradientUnits="userSpaceOnUse">
                                                <stop stopColor="#A78BFA" />
                                                <stop offset="1" stopColor="#EC4899" />
                                            </linearGradient>
                                        </defs>
                                    </svg>
                                    <h3 className="ai-summary-title">{t('models.search.aiAssessment')}</h3>
                                </div>
                            </div>
                            <div className={`ai-summary-content ${aiSummaryExpanded ? "expanded" : ""}`}>
                                <p className="ai-summary-text ai-summary-text--preline">{aiSummary}</p>
                            </div>
                            {aiSummary.length > 200 && (
                                <button
                                    className="ai-summary-toggle"
                                    onClick={() => setAiSummaryExpanded(!aiSummaryExpanded)}
                                >
                                    {aiSummaryExpanded ? "Show less" : "Show more"}
                                </button>
                            )}
                        </div>
                    )}

                    {/* List View */}
                    {!loading && viewMode === "list" && (
                        <div className="search-list-view">
                            <table className="search-table">
                                <thead>
                                    <tr>
                                        <th>Model Name</th>
                                        <th>Last Updated</th>
                                        <th>Value</th>
                                        <th>Currency / Unit</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {searchResults.map((model) => (
                                        <tr
                                            key={model.id}
                                            className="search-table-row"
                                            onClick={() => handleModelClick(model)}
                                        >
                                            <td className="name-cell">
                                                <div className="search-table__name">{model.name}</div>
                                                <div className="search-table__description">{model.description}</div>
                                            </td>
                                            <td>{model.date}</td>
                                            <td>{formatPrice(model.value)}</td>
                                            <td>{model.currencyUnit}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {/* Grid View */}
                    {!loading && viewMode === "grid" && (
                        <div className="search-materials-grid">
                            {searchResults.map((model) => (
                                <div
                                    key={model.id}
                                    className="material-card"
                                    onClick={() => handleModelClick(model)}
                                >
                                    {/* Value and Date */}
                                    <div className="material-header">
                                        <div className="material-price">
                                            {model.value.toLocaleString()} {model.currencyUnit.split(" / ")[0]}
                                        </div>
                                        <div className="material-date">{model.date}</div>
                                    </div>

                                    {/* Chart */}
                                    <div className="material-chart-wrapper">
                                        <MiniChartSVG data={model.chartData} uniqueId={model.id} color="#EB5757" strokeWidth={1} />
                                    </div>

                                    {/* Name */}
                                    <div className="material-footer">
                                        <div className="material-name">{model.name}</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Error message */}
                    {!loading && error && (
                        <div className="error-state">
                            <p>Error searching models: {error.message}</p>
                        </div>
                    )}

                    {/* No results message */}
                    {!loading && !error && searchResults.length === 0 && searchQuery && (
                        <div className="no-results">
                            <p>{t('models.search.noResults')} "{searchQuery}".</p>
                        </div>
                    )}
                </div>

                {/* Right Sidebar */}
                {!loading && matchingCategories.length > 0 && (
                    <div className="search-sidebar">
                        <h3 className="sidebar-title">{t('models.search.matchingCategories')}</h3>
                        <div className="matching-lists">
                            {matchingCategories.map((list) => (
                                <div
                                    key={list.id}
                                    className={`category-card ${selectedCategory === list.name ? "selected" : ""}`}
                                    onClick={() => handleCategoryClick(list.name)}
                                >
                                    <div className="category-name">{translateSectorLabel(list.name, i18n.resolvedLanguage || i18n.language)}</div>
                                    <div className="category-count">{list.count} {t('models.search.models')}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ModelSearchResults;
